/* ════════════════════════════════════════════════════════════════════════
   MOOD-AWARE CLEANING DELEGATION (front-end only)
   Reads mood check-ins + cleaning tasks from HomeOSStateProvider state
   and produces a fair, supportive cleaning plan.

   All copy is intentionally supportive — never punitive.
   ════════════════════════════════════════════════════════════════════════ */

/* Age rank for "age-appropriate" matching */
const AGE_RANK = { Adult: 3, Teen: 2, Child: 1, Baby: 0, Elder: 3, Pet: 0, Other: 2 };

/* Energy penalty for sorting */
const ENERGY_PENALTY = { Low: 0, Medium: 1, High: 2 };

/* Zone → supportive rules */
export const ZONE_RULES = {
  red: {
    badge: "Keep it light",
    badgeCls: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200 border-rose-200 dark:border-rose-800",
    dot: "bg-rose-500",
    note: "Red zone — avoid adding extra cleaning pressure.",
    supportiveLine: "Keep instructions simple today.",
    maxEnergy: "Low",
    maxMinutes: 10,
    approvalOnly: true, // if too many tasks, fall back to "approve only"
  },
  blue: {
    badge: "Gentle task",
    badgeCls: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200 border-sky-200 dark:border-sky-800",
    dot: "bg-sky-500",
    note: "Blue zone — keeping task gentle today.",
    supportiveLine: "Offer support or quiet time.",
    maxEnergy: "Low",
    maxMinutes: 15,
    approvalOnly: false,
  },
  green: {
    badge: "Steady task",
    badgeCls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800",
    dot: "bg-emerald-500",
    note: "Green zone — good for calm, steady tasks.",
    supportiveLine: "Good for calm, steady tasks.",
    maxEnergy: "Medium",
    maxMinutes: 30,
    approvalOnly: false,
  },
  yellow: {
    badge: "Good energy",
    badgeCls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200 border-amber-200 dark:border-amber-800",
    dot: "bg-amber-500",
    note: "Yellow zone — good for active tasks.",
    supportiveLine: "Good energy for active tasks.",
    maxEnergy: "High",
    maxMinutes: 60,
    approvalOnly: false,
  },
  none: {
    badge: "No check-in",
    badgeCls: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300 border-gray-200 dark:border-slate-600",
    dot: "bg-gray-400",
    note: "No mood check-in yet, using normal delegation rules.",
    supportiveLine: "No mood check-in yet, using normal delegation rules.",
    maxEnergy: "High",
    maxMinutes: 60,
    approvalOnly: false,
  },
};

const ENERGY_ORDER = { Low: 0, Medium: 1, High: 2 };

function todayIso() { return new Date().toISOString().slice(0, 10); }

/* ── Mood lookups ───────────────────────────────────────────────────── */

export function getLatestMoodForMember(memberId, moodCheckins) {
  const today = todayIso();
  return (moodCheckins || [])
    .filter(c => c.memberId === memberId && (c.createdAt || "").slice(0, 10) === today)
    .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""))[0] || null;
}

export function getMoodZoneForMember(memberId, moodCheckins) {
  const c = getLatestMoodForMember(memberId, moodCheckins);
  return c?.zone || "none";
}

export function getMoodDelegationRules(zone) {
  return ZONE_RULES[zone] || ZONE_RULES.none;
}

/* ── Scoring ─────────────────────────────────────────────────────────
   Higher score = better match. Negative scores → skip when possible.
   ──────────────────────────────────────────────────────────────────── */

export function scoreCleaningAssignment({ member, task, currentLoad, mood, useMood }) {
  let score = 0;
  let reasons = [];

  // Workload — lighter load is better
  score -= currentLoad * 1.5;
  if (currentLoad <= 1) reasons.push("has fewer tasks today");

  // Energy state from profile
  if (member.energyStatus === "Good") { score += 1; if (!reasons.length) reasons.push("has good energy"); }
  if (member.energyStatus === "Low")  { score -= 1.5; }

  // Age suitability
  const need = AGE_RANK[task.age] || 2;
  const has  = AGE_RANK[member.ageGroup] || 2;
  if (has < need) score -= 4;
  if (has === need) reasons.push("age-appropriate");

  // Energy of the task
  score -= ENERGY_PENALTY[task.energy] || 0;
  if (task.energy === "Low") reasons.push("low-energy job — easy fit");

  // Mood-aware adjustments
  if (useMood && mood && mood.zone) {
    const rules = ZONE_RULES[mood.zone];
    const tooEnergetic = ENERGY_ORDER[task.energy] > ENERGY_ORDER[rules.maxEnergy];
    const tooLong = (task.estimatedMinutes || 0) > rules.maxMinutes;

    if (mood.zone === "red") {
      if (tooEnergetic || tooLong) score -= 20; // very strong avoidance
      if (task.energy === "Low" && (task.estimatedMinutes || 0) <= 10) score += 2;
    }
    if (mood.zone === "blue") {
      if (tooEnergetic) score -= 12;
      if (tooLong) score -= 4;
      if (task.energy === "Low") score += 1.5;
    }
    if (mood.zone === "green") {
      if (task.energy === "High") score -= 4;
      if (task.energy === "Low" || task.energy === "Medium") score += 1;
    }
    if (mood.zone === "yellow") {
      if (task.energy === "High") score += 2;
      if (task.energy === "Medium") score += 1.5;
    }
  }

  return { score, reasons };
}

/* ── Plan generator ──────────────────────────────────────────────────
   Returns an ordered list of assignments + per-member rollup.
   Each assignment may be { kind: "approval" } if ARA decides the best
   help for an overloaded person is to NOT assign them more.
   ──────────────────────────────────────────────────────────────────── */

export function generateMoodAwareCleaningPlan({
  tasks = [],
  members = [],
  moodCheckins = [],
  scope = { roomId: null, timeframe: "today" },
  includeKids = true,
  prioritiseLow = false,
  avoidOverloaded = true,
  useMood = true,
  maxAssignments = 12,
}) {
  // Filter task pool
  let pool = tasks.filter(t => t.status !== "Done");
  if (scope.roomId) pool = pool.filter(t => t.room === scope.roomId);
  if (scope.timeframe === "today")     pool = pool.filter(t => t.frequency === "Daily");
  if (scope.timeframe === "thisWeek")  pool = pool.filter(t => ["Daily", "Weekly"].includes(t.frequency));
  if (scope.timeframe === "deepClean") pool = pool.filter(t => ["Monthly", "Seasonal"].includes(t.frequency));

  if (prioritiseLow) {
    pool = [...pool].sort((a, b) => (a.energy === "Low" ? -1 : 1) - (b.energy === "Low" ? -1 : 1));
  }

  // Eligible members
  const eligible = members.filter(m => {
    if (m.ageGroup === "Baby" || m.ageGroup === "Pet") return false;
    if (!includeKids && m.ageGroup === "Child") return false;
    return true;
  });
  if (eligible.length === 0) return { assignments: [], byMember: [] };

  // Pre-compute mood per member
  const moodByMember = new Map(
    eligible.map(m => [m.id, getLatestMoodForMember(m.id, moodCheckins)])
  );
  const zoneByMember = new Map(
    eligible.map(m => [m.id, moodByMember.get(m.id)?.zone || "none"])
  );

  // Workload tracker
  const load = new Map(eligible.map(m => [m.id, m.tasksToday || 0]));

  // Find members in RED zone — if their load already high, switch them to approval-only
  const approvalOnly = new Set();
  if (useMood) {
    eligible.forEach(m => {
      const zone = zoneByMember.get(m.id);
      const loadVal = load.get(m.id) || 0;
      if (zone === "red" && (loadVal >= 4 || avoidOverloaded && loadVal >= 3)) {
        approvalOnly.add(m.id);
      }
    });
  }

  const assignments = [];

  pool.forEach(task => {
    // Build candidate list (skip approval-only members)
    const candidates = eligible.filter(m => !approvalOnly.has(m.id));
    if (candidates.length === 0) return;

    const ranked = candidates
      .map(m => {
        const { score, reasons } = scoreCleaningAssignment({
          member: m,
          task,
          currentLoad: load.get(m.id) || 0,
          mood: moodByMember.get(m.id),
          useMood,
        });
        const loadVal = load.get(m.id) || 0;
        return { member: m, score: score - (avoidOverloaded ? loadVal * 0.5 : 0), reasons };
      })
      .sort((a, b) => b.score - a.score);

    const winner = ranked[0];
    if (!winner) return;

    const zone = zoneByMember.get(winner.member.id);
    const rules = ZONE_RULES[zone];
    const mood = moodByMember.get(winner.member.id);

    const why = winner.reasons.length
      ? winner.reasons.slice(0, 2).map(r => r.charAt(0).toUpperCase() + r.slice(1)).join(". ") + "."
      : "Available and balanced across the week.";

    assignments.push({
      kind: "task",
      taskId: task.id,
      title: task.title,
      room: task.room,
      assignedTo: winner.member.id,
      assignedName: winner.member.name,
      energy: task.energy,
      minutes: task.estimatedMinutes,
      frequency: task.frequency,
      why,
      moodZone: zone,
      moodLabel: mood?.moodLabel || null,
      moodNote: rules.note,
      moodBadge: rules.badge,
    });
    load.set(winner.member.id, (load.get(winner.member.id) || 0) + 1);
  });

  // Approval-only entries (one per member who was protected)
  approvalOnly.forEach(memberId => {
    const m = eligible.find(x => x.id === memberId);
    if (!m) return;
    const mood = moodByMember.get(memberId);
    const rules = ZONE_RULES.red;
    assignments.push({
      kind: "approval",
      taskId: `approval-${memberId}`,
      title: "Approve plan only",
      room: null,
      assignedTo: memberId,
      assignedName: m.name,
      energy: "Low",
      minutes: 2,
      frequency: "—",
      why: "ARA is reducing mental load because workload is high right now.",
      moodZone: "red",
      moodLabel: mood?.moodLabel || null,
      moodNote: rules.note,
      moodBadge: rules.badge,
    });
  });

  // Cap and build per-member rollup
  const capped = assignments.slice(0, maxAssignments);
  const byMember = eligible
    .map(m => ({
      ...m,
      moodZone: zoneByMember.get(m.id),
      moodLabel: moodByMember.get(m.id)?.moodLabel || null,
      count: capped.filter(a => a.assignedTo === m.id && a.kind === "task").length,
      approvalOnly: approvalOnly.has(m.id),
    }))
    .filter(x => x.count > 0 || x.approvalOnly);

  return { assignments: capped, byMember };
}

/* ── Insight detectors for "ARA noticed…" ───────────────────────────── */

export function detectMoodCleaningInsights({ moodCheckins, members, cleaningTasks }) {
  const today = todayIso();
  const todays = (moodCheckins || []).filter(c => (c.createdAt || "").slice(0, 10) === today);
  const seen = new Set();
  const latest = todays.filter(c => { if (seen.has(c.memberId)) return false; seen.add(c.memberId); return true; });

  const adults = (members || []).filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet");
  const checkedIn = latest.length;
  const total = adults.length;
  const overloaded = latest.filter(c => c.zone === "red");
  const lowEnergy  = latest.filter(c => c.zone === "blue");
  const openCleaning = (cleaningTasks || []).filter(t => t.status !== "Done").length;

  const insights = [];
  if (overloaded.length > 0) {
    const m = (members || []).find(x => x.id === overloaded[0].memberId);
    const name = m?.name || "Someone";
    const moodWord = (overloaded[0].moodLabel || "overwhelmed").toLowerCase();
    insights.push({
      id: "mood-overloaded",
      text: `${name} is feeling ${moodWord} — ARA will keep their cleaning load light today.`,
      action: { label: "Open Cleaning", route: "/home-os/cleaning" },
    });
  }
  if (lowEnergy.length >= 2 && openCleaning > 8) {
    insights.push({
      id: "mood-low-energy-household",
      text: "Today looks like a low-energy household day. Use Daily Reset only.",
      action: { label: "Open Cleaning", route: "/home-os/cleaning" },
    });
  }
  if (total - checkedIn >= 2) {
    insights.push({
      id: "mood-uncheckedin",
      text: `${total - checkedIn} people haven't checked in yet — mood-aware delegation will be less accurate.`,
      action: { label: "Check in", route: "/home-os/mood" },
    });
  }
  if (openCleaning > 8 && checkedIn > 0) {
    insights.push({
      id: "cleaning-rebalance",
      text: "Cleaning load looks uneven. ARA can rebalance it using mood check-ins.",
      action: { label: "Delegate", route: "/home-os/cleaning" },
    });
  }
  return insights;
}
